/**
 * Origin Cell Utilities
 *
 * Provides data transformation logic for the OriginCell component,
 * specifically handling origin prioritization based on active filters.
 *
 * @module originCell.util
 * @see OriginCell.tsx - Component that uses this utility
 * @see statisticValidation.util.ts - normalizeOriginName function
 */

import { normalizeOriginName } from '@/utils/statisticValidation.util';

/**
 * Default origin that receives priority when no filters are active
 */
export const DEFAULT_PRIORITY_ORIGIN = 'chariot';

/**
 * Prioritizes origin sources based on active table filters
 *
 * Priority Rules:
 * 1. No filters: DEFAULT_PRIORITY_ORIGIN first, others alphabetically
 * 2. With filters: Filtered origins first (alphabetical), then others
 *
 * @param normalizedOrigins - Already normalized and deduplicated origin strings
 * @param activeFilters - Currently selected origin filters from table state
 * @returns Prioritized and sorted array of origins
 *
 * @example
 * ```typescript
 * // No filters - chariot first
 * getPrioritizedOrigins(['AWS', 'Chariot', 'Azure'])
 * // => ['Chariot', 'AWS', 'Azure']
 *
 * // With filters - filtered origins first
 * getPrioritizedOrigins(['AWS', 'Chariot', 'Azure'], ['aws'])
 * // => ['AWS', 'Azure', 'Chariot']
 * ```
 */
export const getPrioritizedOrigins = (
  normalizedOrigins: readonly string[],
  activeFilters?: string[]
): string[] => {
  if (!normalizedOrigins.length) return [];

  // No filters active: Default origin first, rest alphabetically
  if (!activeFilters || activeFilters.length === 0) {
    const defaultIndex = normalizedOrigins.findIndex(
      origin => origin.toLowerCase() === DEFAULT_PRIORITY_ORIGIN
    );

    if (defaultIndex > -1) {
      const defaultOrigin = normalizedOrigins[defaultIndex];
      const otherOrigins = normalizedOrigins
        .filter((_, i) => i !== defaultIndex)
        .sort();
      return [defaultOrigin, ...otherOrigins];
    }

    // No default origin found, return sorted copy
    return [...normalizedOrigins].sort();
  }

  // Filters active: Normalize filters and split into tiers
  const normalizedFilters = activeFilters.map(filter =>
    normalizeOriginName(filter).toLowerCase()
  );
  const filteredSet = new Set(normalizedFilters);

  const matchedOrigins = normalizedOrigins
    .filter(origin => filteredSet.has(origin.toLowerCase()))
    .sort();

  const unmatchedOrigins = normalizedOrigins
    .filter(origin => !filteredSet.has(origin.toLowerCase()))
    .sort();

  return [...matchedOrigins, ...unmatchedOrigins];
};
